import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { AlertTriangle, FilePlus2, Link2 } from "lucide-react";
import { useEffect, useState } from "react";

import { errorMessage } from "@/lib/api";
import type { DrawingMatchOutcome } from "../api";
import type { DrawingsPanelOps, PanelMatchResult } from "./types";

/**
 * 選んだ図面を明細に紐付けるダイアログ。
 *
 * **押す前に何が起きるかを見せる。** 以前は「突き合わせる」を押すといきなり実行し、
 * 終わってから結果を 1 行の文で知らせていた。紐付いた数と、明細が勝手に増えた数が
 * 後からしか分からず、戻すのも手間だった。
 *
 * **判定はサーバーの下見（dry run）に任せる。** 画面側で図番を照合すると、
 * 「同じ図番の明細が複数ある」「既に別の図面が付いている」といった規則を二重に
 * 持つことになり、ずれると**見せた内容と違う結果**になる。下見は本番と同じ処理を
 * 通して何も保存しないので、ここで見せた内容がそのまま起きる。
 *
 * 一致する明細が無い図面（`no_item`）は**既定では何もしない**。行を立てるかどうかは
 * 人が決めることなので、チェックを入れたときだけ作る。
 */
export default function DrawingMatchDialog({
  isOpen,
  ops,
  drawingIds,
  onClose,
  onDone,
}: {
  isOpen: boolean;
  ops: DrawingsPanelOps;
  /** 画面で選ばれている図面。 */
  drawingIds: number[];
  onClose: () => void;
  /** 実行が終わったときに呼ぶ（一覧の取り直しと知らせは呼び出し元が行う）。 */
  onDone: (message: string) => void;
}) {
  const [preview, setPreview] = useState<PanelMatchResult[] | null>(null);
  const [createRest, setCreateRest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 開くたびに下見をやり直す。前回の結果を持ち越すと、閉じている間に変わった
  // 明細が反映されないまま実行してしまう。
  useEffect(() => {
    if (!isOpen) return;
    setPreview(null);
    setCreateRest(false);
    setError(null);
    setBusy(false);
    let cancelled = false;
    ops
      .match(drawingIds, true)
      .then((res) => !cancelled && setPreview(res.results))
      .catch((e) => !cancelled && setError(errorMessage(e, "下見に失敗しました。")));
    return () => {
      cancelled = true;
    };
    // drawingIds は毎描画で作り直される配列。中身が同じなら張り直す必要は無い。
    // ops も同じ理由で依存に入れない（持ち主が変われば画面ごと作り直される）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, drawingIds.join(",")]);

  const count = (outcome: DrawingMatchOutcome) =>
    preview?.filter((r) => r.outcome === outcome).length ?? 0;
  const linked = count("linked");
  const noItem = count("no_item");
  const ambiguous = count("ambiguous");
  const noNumber = count("no_drawing_number");
  /** 押しても何も起きない状態。ボタンを無効にして空振りを防ぐ。 */
  const nothingToDo = linked === 0 && !(createRest && noItem > 0);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const done: string[] = [];
      if (linked > 0) {
        await ops.match(drawingIds);
        done.push(`${linked} 件を明細に紐付けました`);
      }
      if (createRest && noItem > 0) {
        const targets = (preview ?? [])
          .filter((r) => r.outcome === "no_item")
          .map((r) => r.drawing_id);
        const res = await ops.createItems(targets);
        done.push(`${res.created_count} 件は明細を作って紐付けました`);
      }
      onDone(`${done.join("。")}。`);
      onClose();
    } catch (e) {
      setError(errorMessage(e, "紐付けに失敗しました。"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="lg">
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>明細と紐づけ</span>
          <span className="text-small font-normal text-default-500">
            選んだ {drawingIds.length} 件の図番を、{ops.matchDescription}
          </span>
        </ModalHeader>

        <ModalBody>
          {preview == null && error == null ? (
            <div className="flex justify-center py-6">
              <Spinner label="突き合わせています..." />
            </div>
          ) : (
            <div className="flex flex-col gap-3 text-sm" data-testid="match-preview">
              <p className="flex items-center gap-2 rounded-md bg-default-50 p-3">
                <Link2 size={16} className="shrink-0 text-brand-500" />
                <span>
                  <span className="font-bold">{linked} 件</span>
                  を明細に紐付けます
                </span>
              </p>

              {noItem > 0 && (
                <div className="rounded-md bg-warning-50 p-3 text-warning-700">
                  <p className="mb-2 flex items-start gap-2">
                    <FilePlus2 size={16} className="mt-0.5 shrink-0" />
                    <span>
                      <span className="font-bold">{noItem} 件</span>
                      は、図番に一致する明細がありません。
                    </span>
                  </p>
                  <Checkbox
                    size="sm"
                    isSelected={createRest}
                    onValueChange={setCreateRest}
                    data-testid="match-create-rest"
                  >
                    この {noItem} 件は明細を作って紐づける
                  </Checkbox>
                </div>
              )}

              {(ambiguous > 0 || noNumber > 0) && (
                <div className="rounded-md bg-default-50 p-3 text-default-600">
                  <p className="mb-1 flex items-center gap-2 font-bold">
                    <AlertTriangle size={16} />
                    ここでは決められないもの
                  </p>
                  <ul className="list-disc pl-5">
                    {ambiguous > 0 && (
                      <li>
                        {ambiguous}{" "}
                        件は図番が一致する明細が複数あります（カードを開いて選んでください）
                      </li>
                    )}
                    {noNumber > 0 && (
                      <li>
                        {noNumber} 件は図番が未入力です（カードを開いて入れてください）
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="match-submit"
            isDisabled={preview == null || nothingToDo}
            isLoading={busy}
            onPress={() => void run()}
          >
            紐づける
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
